import React, { useEffect, useState } from "react";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AddIcon from "@mui/icons-material/Add";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Card, Chip, Typography } from "@mui/material";
import PageHeader from "../../components/page-header/page-header";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import "./dashboard.css";

// Dashboard-specific types
interface ChartDataPoint {
  name: string;
  value: number;
  [key: string]: any;
}

interface StatCardProps {
  title: string;
  value: number | string;
  trend: "up" | "down" | "neutral";
  trendValue: number;
  chartData: ChartDataPoint[];
}

interface PieChartLabelProps {
  name: string;
  value: number;
  percent: number;
}

// Minimalistic colors for charts
const COLORS: string[] = [
  "#6b7280", // gray
  "#9ca3af", // light gray
  "#d1d5db", // lighter gray
  "#e5e7eb", // very light gray
  "#f3f4f6", // almost white
  "#9ca3af", // light gray
];

// Chart color for all stat cards
const STAT_CHART_COLOR = "rgba(107, 114, 128, 0.3)"; // subtle gray

// Custom tooltip styling
const tooltipStyle: React.CSSProperties = {
  backgroundColor: "var(--card-bg, #f0f4f8)",
  borderRadius: "12px",
  color: "var(--text-primary, #334155)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  padding: "10px 14px",
  fontSize: "0.875rem",
  fontWeight: 500,
  border: "none",
};

// Custom label styling for pie charts
const renderCustomizedPieLabel = ({
  name,
  value,
  percent,
}: PieChartLabelProps): string => {
  return percent > 0.1 ? `${(percent * 100).toFixed(0)}%` : "";
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  trend,
  trendValue,
  chartData,
}) => {
  const getTrendIcon = () => {
    switch (trend) {
      case "up":
        return <TrendingUpIcon sx={{ fontSize: 16, color: "#059669" }} />;
      case "down":
        return <TrendingDownIcon sx={{ fontSize: 16, color: "#dc2626" }} />;
      default:
        return null;
    }
  };

  const getTrendColor = (): string => {
    switch (trend) {
      case "up":
        return "#059669";
      case "down":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  return (
    <Card className="stat-card">
      <Box className="stat-content">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1rem",
          }}
        >
          <Typography variant="h6" className="stat-label">
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {getTrendIcon()}
            <Typography
              variant="body2"
              sx={{
                color: getTrendColor(),
                fontWeight: 700,
                fontSize: "0.875rem",
              }}
            >
              {trendValue > 0 ? "+" : ""}
              {trendValue}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="h4" className="stat-value">
          {value}
        </Typography>
        <Box className="stat-chart">
          <ResponsiveContainer width="100%" height={60}>
            <AreaChart data={chartData}>
              <Area
                type="monotone"
                dataKey="value"
                stroke={STAT_CHART_COLOR}
                fill={STAT_CHART_COLOR}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<{
    totalCars: number;
    totalBrands: number;
    totalModels: number;
    totalReviews: number;
    monthlyStats: ChartDataPoint[];
    brandDistribution: ChartDataPoint[];
    priceRanges: ChartDataPoint[];
    recentActivity: ChartDataPoint[];
  }>({
    totalCars: 0,
    totalBrands: 0,
    totalModels: 0,
    totalReviews: 0,
    monthlyStats: [],
    brandDistribution: [],
    priceRanges: [],
    recentActivity: [],
  });

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Mock data for demonstration - replace with actual API calls
    const fetchDashboardData = async (): Promise<void> => {
      try {
        setLoading(true);

        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Mock dashboard data
        setDashboardData({
          totalCars: 156,
          totalBrands: 12,
          totalModels: 45,
          totalReviews: 89,
          monthlyStats: [
            { name: "Янв", value: 12 },
            { name: "Фев", value: 19 },
            { name: "Мар", value: 15 },
            { name: "Апр", value: 25 },
            { name: "Май", value: 22 },
            { name: "Июн", value: 30 },
          ],
          brandDistribution: [
            { name: "Toyota", value: 35 },
            { name: "BMW", value: 25 },
            { name: "Mercedes", value: 20 },
            { name: "Audi", value: 15 },
            { name: "Другие", value: 5 },
          ],
          priceRanges: [
            { name: "До 1М", value: 45 },
            { name: "1-3М", value: 65 },
            { name: "3-5М", value: 30 },
            { name: "5М+", value: 16 },
          ],
          recentActivity: [
            { name: "Пн", value: 8 },
            { name: "Вт", value: 12 },
            { name: "Ср", value: 6 },
            { name: "Чт", value: 15 },
            { name: "Пт", value: 10 },
            { name: "Сб", value: 4 },
            { name: "Вс", value: 2 },
          ],
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard__loading">
          <div className="dashboard__loading-spinner"></div>
          <Typography
            variant="h6"
            sx={{ color: "var(--text-primary)", fontWeight: 600 }}
          >
            Загрузка статистики...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard__content">
        <PageHeader
          title="Статистика"
          subtitle="Обзор производительности и ключевых метрик"
        />

        {/* Quick Actions */}
        <div className="dashboard__quick-actions">
          <div className="quick-action-card">
            <div className="quick-action-icon">
              <AddIcon />
            </div>
            <div className="quick-action-title">Добавить автомобиль</div>
            <div className="quick-action-description">Создать новую запись</div>
          </div>
          <div className="quick-action-card">
            <div className="quick-action-icon">
              <AssessmentIcon />
            </div>
            <div className="quick-action-title">Аналитика</div>
            <div className="quick-action-description">Подробные отчеты</div>
          </div>
          <div className="quick-action-card">
            <div className="quick-action-icon">
              <PeopleIcon />
            </div>
            <div className="quick-action-title">Пользователи</div>
            <div className="quick-action-description">Управление доступом</div>
          </div>
          <div className="quick-action-card">
            <div className="quick-action-icon">
              <SettingsIcon />
            </div>
            <div className="quick-action-title">Настройки</div>
            <div className="quick-action-description">Конфигурация системы</div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="dashboard__stats">
          <StatCard
            title="Всего автомобилей"
            value={dashboardData.totalCars}
            trend="up"
            trendValue={12}
            chartData={dashboardData.monthlyStats}
          />
          <StatCard
            title="Брендов"
            value={dashboardData.totalBrands}
            trend="neutral"
            trendValue={0}
            chartData={dashboardData.brandDistribution}
          />
          <StatCard
            title="Моделей"
            value={dashboardData.totalModels}
            trend="up"
            trendValue={8}
            chartData={dashboardData.priceRanges}
          />
          <StatCard
            title="Отзывов"
            value={dashboardData.totalReviews}
            trend="up"
            trendValue={15}
            chartData={dashboardData.recentActivity}
          />
        </div>

        {/* Insights */}
        <div className="dashboard__insights">
          <Typography
            variant="h6"
            sx={{
              marginBottom: "1.5rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            Ключевые инсайты
          </Typography>
          <div className="insight-item">
            <div className="insight-icon insight-icon--positive">
              <CheckCircleIcon />
            </div>
            <div className="insight-content">
              <div className="insight-title">Рост продаж на 12%</div>
              <div className="insight-description">
                По сравнению с прошлым месяцем
              </div>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon insight-icon--warning">
              <WarningIcon />
            </div>
            <div className="insight-content">
              <div className="insight-title">Низкий рейтинг отзывов</div>
              <div className="insight-description">
                Требуется внимание к качеству
              </div>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon insight-icon--info">
              <InfoIcon />
            </div>
            <div className="insight-content">
              <div className="insight-title">Новые бренды</div>
              <div className="insight-description">
                3 новых производителя добавлены
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="dashboard__charts">
          <Card className="dashboard__chart">
            <div className="dashboard__chart-header">
              <Typography variant="h6" className="dashboard__chart-title">
                Распределение по брендам
              </Typography>
              <div className="dashboard__chart-period">
                <Chip
                  label="За месяц"
                  className="dashboard__period-active"
                  size="small"
                />
              </div>
            </div>
            <div className="dashboard__pie-chart">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.brandDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedPieLabel}
                    outerRadius={80}
                    fill="#6b7280"
                    dataKey="value"
                  >
                    {dashboardData.brandDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={tooltipStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="dashboard__chart">
            <div className="dashboard__chart-header">
              <Typography variant="h6" className="dashboard__chart-title">
                Ценовые диапазоны
              </Typography>
              <div className="dashboard__chart-period">
                <Chip
                  label="За месяц"
                  className="dashboard__period-active"
                  size="small"
                />
              </div>
            </div>
            <div className="dashboard__bar-chart">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.priceRanges}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="value" fill="#6b7280" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
